import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery } from 'urql';
import { Plus, Search, User } from 'lucide-react';
import Swal from 'sweetalert2';
import ModalBase from '@/components/Modal/ModalBase.jsx';
import ProveedorForm from '@/pages/Admin/Proveedores/ProveedorForm.jsx';
import Field from '@/components/common/Field.jsx';
import { Q_PROVEEDORES, M_CREATE_PROVEEDOR } from '@/graphql/suppliers.gql.js';
import { purchaseFormActions, usePurchaseForm } from '@/state/forms/purchaseFormSlice.js';
import { withIdempotencyKey } from '@/utils/idempotency.js';

const actions = purchaseFormActions();

const condicionIvaLabels = {
  RI: 'Responsable Inscripto',
  MT: 'Monotributo',
  EX: 'Exento',
  CF: 'Consumidor Final',
  RNI: 'Responsable No Inscripto',
};

export default function ProveedorSelect() {
  const supplier = usePurchaseForm(state => state.supplier);
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [isModalOpen, setModalOpen] = useState(false);

  useEffect(() => {
    const handle = setTimeout(() => setDebounced(search.trim()), 300);
    return () => clearTimeout(handle);
  }, [search]);

  const [result] = useQuery({
    query: Q_PROVEEDORES,
    variables: {
      filter: {
        search: debounced || undefined,
        estado: 'ACTIVO',
        limit: 5,
        offset: 0,
        orderBy: 'razon_social_ASC',
      },
    },
    requestPolicy: 'cache-and-network',
  });

  const [, createSupplier] = useMutation(M_CREATE_PROVEEDOR);

  const suggestions = useMemo(() => result.data?.proveedores?.items ?? [], [result.data]);

  const handleSelect = item => {
    actions.setSupplier(item);
  };

  const handleCreate = async input => {
    const response = await createSupplier({ input }, withIdempotencyKey('proveedor-create'));
    if (response.error) {
      console.error('Error creando proveedor:', response.error); // eslint-disable-line no-console
      await Swal.fire('No pudimos crear el proveedor', response.error.message, 'error');
      return;
    }
    const created = response.data?.proveedorCreate;
    if (created) {
      actions.setSupplier(created);
    }
    setModalOpen(false);
  };

  return (
    <div className="flex flex-col gap-3">
      <Field label="Proveedor" required>
        <div className="relative">
          <div className="flex items-center gap-3 rounded-xl border border-[rgba(var(--border),0.6)] bg-[rgba(var(--panel),0.6)] px-4 py-3 focus-within:border-[rgb(var(--brand))] focus-within:ring-2 focus-within:ring-[rgb(var(--ring))]">
            <Search className="h-4 w-4 text-[rgba(var(--muted),0.85)]" aria-hidden="true" />
            <input
              type="text"
              value={supplier ? `${supplier.razon_social} (${supplier.cuit})` : search}
              onChange={event => {
                actions.setSupplier(null);
                setSearch(event.target.value);
              }}
              placeholder="Buscá por razón social o CUIT"
              className="w-full bg-transparent text-sm text-[rgb(var(--fg))] placeholder:text-[rgba(var(--muted),0.75)] focus:outline-none"
            />
            <button
              type="button"
              onClick={() => setModalOpen(true)}
              className="inline-flex items-center gap-2 rounded-xl bg-[rgb(var(--brand))] px-3 py-1.5 text-xs font-semibold text-white shadow-[0_10px_24px_rgba(var(--shadow),0.14)] transition hover:bg-[rgb(var(--brand-700))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
            >
              <Plus className="h-3.5 w-3.5" aria-hidden="true" />
              Nuevo
            </button>
          </div>
          {supplier ? (
            <div className="mt-3 flex items-center gap-3 rounded-xl border border-[rgba(var(--border),0.6)] bg-[rgba(var(--panel),0.6)] px-4 py-3 text-sm text-[rgba(var(--muted),0.85)]">
              <User className="h-4 w-4 text-[rgb(var(--brand))]" aria-hidden="true" />
              <div className="flex flex-col">
                <span className="font-semibold text-[rgb(var(--fg))]">{supplier.razon_social}</span>
                <span>CUIT {supplier.cuit}</span>
                <span className="text-xs">
                  {supplier.contacto_email || 'Sin email'} · {supplier.contacto_tel || 'Sin teléfono'}
                </span>
              </div>
              <button
                type="button"
                onClick={() => actions.setSupplier(null)}
                className="ml-auto text-xs font-semibold text-[rgb(var(--brand))] underline-offset-4 hover:underline"
              >
                Cambiar
              </button>
            </div>
          ) : null}
          {!supplier && search && suggestions.length ? (
            <ul className="absolute z-10 mt-2 w-full rounded-2xl border border-[rgba(var(--border),0.6)] bg-[rgba(var(--panel),0.95)] shadow-[0_18px_32px_rgba(var(--shadow),0.16)]">
              {suggestions.map(item => (
                <li key={item.id}>
                  <button
                    type="button"
                    onClick={() => handleSelect(item)}
                    className="flex w-full flex-col items-start gap-1 rounded-2xl px-4 py-3 text-left text-sm text-[rgb(var(--fg))] transition hover:bg-[rgba(var(--panel),0.75)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
                  >
                    <span className="font-semibold">{item.razon_social}</span>
                    <span className="text-xs text-[rgba(var(--muted),0.85)]">CUIT {item.cuit}</span>
                    <span className="text-xs text-[rgba(var(--muted),0.75)]">
                      {condicionIvaLabels[item.condicion_iva] || item.condicion_iva}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      </Field>

      <ModalBase
        isOpen={isModalOpen}
        onClose={() => setModalOpen(false)}
        labelledBy="nuevo-proveedor-modal"
      >
        <div className="space-y-6">
          <header className="flex items-center justify-between">
            <div>
              <h2 id="nuevo-proveedor-modal" className="text-lg font-semibold text-[rgb(var(--fg))]">
                Nuevo proveedor
              </h2>
              <p className="text-sm text-[rgba(var(--muted),0.9)]">
                Completá los datos y se asignará automáticamente a la compra.
              </p>
            </div>
            <button
              type="button"
              onClick={() => setModalOpen(false)}
              className="rounded-xl border border-[rgba(var(--border),0.45)] bg-[rgba(var(--panel),0.7)] px-3 py-1 text-xs font-semibold text-[rgb(var(--fg))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
            >
              Cerrar
            </button>
          </header>
          <ProveedorForm
            onSubmit={handleCreate}
            onCancel={() => setModalOpen(false)}
            submitLabel="Crear"
            cancelLabel="Cancelar"
            showStickyActions={false}
          />
        </div>
      </ModalBase>
    </div>
  );
}
